import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Checkbox } from '../../../Checkbox';
import { ThermostatActions } from '../../../../store';
import './ThermostatRestore.css';
import { PrimaryButton } from '../../../PrimaryButton';
import { ThermostatInfo } from '../ThermostatInfo';
import { Notification } from '../../../Notification';
import { Modal } from '../../../Modal';

export class ThermostatRestore extends Component {
  state = {
    showInfoPage: true,
    checkedItems: { schedule: false, settings: false },
  };

  handleModalClose = () => {
    this.props.onRequestClose();
  };

  handleCheckboxChange(e) {
    const { id, checked } = e.target;

    this.setState((prevState) => ({
      ...prevState,
      checkedItems: {
        ...prevState.checkedItems,
        [id]: checked,
      },
    }));
  }

  closeInfo = () => {
    this.setState((state) => ({
      ...state,
      showInfoPage: false,
    }));
  };

  applyChanges = async () => {
    const { schedule, settings } = this.state.checkedItems;
    const { uid_hash: uidHash } = this.props.currentThermostat;
    const { pushSchedule, pushSettings, clearRestoreResults } = this.props;

    clearRestoreResults();

    if (schedule && settings) {
      return Promise.all([pushSchedule(uidHash), pushSettings(uidHash)]);
    }

    if (schedule) {
      return pushSchedule(uidHash);
    }

    return pushSettings(uidHash);
  };

  renderRestorePage() {
    const { checkedItems } = this.state;
    const {
      thermostatError,
      currentThermostat: { connection_state: connectionState },
    } = this.props;

    return (
      <div className="thermostat-restore__content">
        <div className="thermostat-restore__text-group">
          <div>Select defaults to restore:</div>
        </div>
        <div className="thermostat-restore__options">
          <label
            htmlFor="schedule"
            className={
              checkedItems.schedule
                ? 'thermostat-restore__option thermostat-restore__option--selected'
                : 'thermostat-restore__option'
            }
          >
            <div className="thermostat-restore__option__text">Schedule</div>
            <Checkbox id="schedule" checked={checkedItems.schedule} onChange={(e) => this.handleCheckboxChange(e)} />
          </label>
          <label
            htmlFor="settings"
            className={
              checkedItems.settings
                ? 'thermostat-restore__option thermostat-restore__option--selected'
                : 'thermostat-restore__option'
            }
          >
            <div className="thermostat-restore__option__text">Settings</div>
            <Checkbox id="settings" checked={checkedItems.settings} onChange={(e) => this.handleCheckboxChange(e)} />
          </label>
        </div>
        <div className="thermostat-restore__primary-button-wrapper">
          <PrimaryButton
            icon="save-icon"
            label="Save Changes"
            isDisabled={!(checkedItems.schedule || checkedItems.settings)}
            onClick={this.applyChanges}
            fullWidth={true}
          />
        </div>
        <div className="thermostat-restore__notification">
          {connectionState === 'offline' && (
            <Notification
              title="No Wifi Connection Available."
              message="Changes will not take effect until wifi is restored to this thermostat."
              type="warning"
            />
          )}
        </div>
        <div className="thermostat-restore__error" style={thermostatError ? {} : { visibility: 'hidden' }}>
          <Notification message={thermostatError} type="error" />
        </div>
      </div>
    );
  }

  renderRestoreResults() {
    const { restoreMessage } = this.props;

    return (
      <div className="modal-page-restore">
        <div className="modal-page-restore__text-group">
          <div className="thermostat-restore__title-text">Finished!</div>
        </div>

        <div className="thermostat-restore__success">
          <Notification message={restoreMessage} type="success" />
        </div>
        <div className="thermostat-restore__primary-button-wrapper">
          <PrimaryButton icon="back-icon" label="View Thermostats" onClick={this.handleModalClose} fullWidth={true} />
        </div>
      </div>
    );
  }

  renderCorrectRestorePage(currentThermostat) {
    const { restoreMessage, isRestoring } = this.props;

    if (!isRestoring && restoreMessage) {
      return this.renderRestoreResults();
    }

    return this.renderRestorePage(currentThermostat);
  }

  render() {
    const { showInfoPage } = this.state;
    const { currentThermostat, selectedBuilding } = this.props;

    return (
      <React.Fragment>
        {showInfoPage ? (
          <ThermostatInfo
            currentBuilding={selectedBuilding}
            currentThermostat={currentThermostat}
            onClose={this.handleModalClose}
            mainButtonOnClick={this.closeInfo}
          />
        ) : null}
        <Modal>
          <div className="thermostat-restore-overlay">
            <div className="thermostat-restore-modal">
              <div className="thermostat-restore__title">
                <div className="thermostat-restore__back-button-div" />
                <div className="thermostat-restore__title-div">
                  <div className="thermostat-restore__title-text">Restore Thermostat</div>
                </div>
                <div className="thermostat-restore__close-button-div">
                  <input
                    className="thermostat-restore__close-button"
                    type="button"
                    id="close-button"
                    onClick={this.handleModalClose}
                  />
                </div>
              </div>

              {this.renderCorrectRestorePage(currentThermostat)}
            </div>
          </div>
        </Modal>
      </React.Fragment>
    );
  }
}

ThermostatRestore.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  currentThermostat: PropTypes.shape({
    mode: PropTypes.string,
    state: PropTypes.string,
    uid_hash: PropTypes.string,
    fan_mode: PropTypes.string,
    direction: PropTypes.string,
    is_occupied: PropTypes.bool,
    unit_number: PropTypes.string,
    display_name: PropTypes.string,
    floor_number: PropTypes.number,
    serial_number: PropTypes.string,
    building_slug: PropTypes.string,
    is_fan_running: PropTypes.bool,
    thermostat_type: PropTypes.string,
    building_section: PropTypes.string,
    temperature_unit: PropTypes.string,
    connection_state: PropTypes.string,
    target_temperature: PropTypes.string,
    ambient_temperature: PropTypes.string,
    connection_timestamp: PropTypes.string,
    target_temperature_low: PropTypes.string,
    target_temperature_high: PropTypes.string,
    disconnection_timestamp: PropTypes.string,
    status_update_timestamp: PropTypes.string,
    temperature_satisfaction: PropTypes.string,
  }).isRequired,
  selectedBuilding: PropTypes.string.isRequired,
  pushSettings: PropTypes.func.isRequired,
  pushSchedule: PropTypes.func.isRequired,
  isRestoring: PropTypes.bool.isRequired,
  thermostatError: PropTypes.string,
  restoreMessage: PropTypes.string,
};

ThermostatRestore.defaultProps = {
  thermostatError: undefined,
  restoreMessage: undefined,
};

export function mapStateToProps(state) {
  return {
    thermostatError: state.thermostatReducer.error,
    restoreMessage: state.thermostatReducer.restoreMessage,
    isRestoring: state.thermostatReducer.isRestoring,
  };
}

const mapDispatchToProps = {
  refreshPin: ThermostatActions.refreshPin,
  pushSettings: ThermostatActions.pushSettings,
  pushSchedule: ThermostatActions.pushSchedule,
  clearRestoreResults: ThermostatActions.clearRestoreResults,
};

export const ConnectedThermostatRestore = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ThermostatRestore);
